import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import {
	ChecklistItemCard,
	PlacementFrontend,
	type ChecklistItemCardProps,
} from '@post-office/browser--growth-starburst/wac-product-activation-checklist';

import { useCallback, useEffect, useMemo } from 'react';

type RenderProps = ChecklistItemCardProps;

const reportMessageInstanceEventError = (error: unknown) => {
	if (typeof globalThis.reportError === 'function') {
		globalThis.reportError(error);
	}
};

const ChecklistItem = (context: RenderProps) => {
	const { actionType, completed, cta, id, onClick } = context;
	const { messageInstanceViewed } = useMessageInstanceEvent();
	const { onCtaActioned } = usePostOfficeUserInteractions();
	const analyticsMetadata = useMemo(
		() => ({
			itemId: id,
			actionType,
			completed,
		}),
		[actionType, completed, id],
	);

	useEffect(() => {
		messageInstanceViewed(analyticsMetadata).catch(reportMessageInstanceEventError);
	}, [analyticsMetadata, messageInstanceViewed]);

	const handleCtaClick = useCallback(() => {
		if (!cta || completed) {
			return;
		}

		onCtaActioned({
			interactionIdentifier: id,
			destinationUrl: cta.href,
			trackingLinkId: id,
			additionalMetadata: analyticsMetadata,
		});

		onClick?.();
	}, [analyticsMetadata, completed, cta, id, onCtaActioned, onClick]);

	return <ChecklistItemCard {...context} onClick={handleCtaClick} />;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<RenderProps>(),
		Component: ChecklistItem,
	},
});
